// ---------------------------------------------------------------------------
// Serien (PLAN.md §43): Die Geschichten einer Serie sind ihre Episoden, in fester Reihenfolge. Ein Element lässt sich in andere Episoden
// übernehmen – als **Referenz**: eine verknüpfte Kopie mit gemeinsamem Steckbrief (beim Anlegen einer Episode gewählt oder importiert).
// Was am Ende der früheren Episoden über ein Element feststeht (Zustände, Lebensdaten, Beziehungen, Wissen), ist seine Vorgeschichte in
// der Episode (`Story.series`): die Ausgangslage, von der Zustand, Alter, Beziehungen und Wissen ausgehen. Nach hinten wirkt nichts: Was
// in einer Episode geschieht, ändert die früheren nicht.
// ---------------------------------------------------------------------------

import type { StoryCtx } from './ctx.js';
import { knowledgeOf } from './knowledge.js';
import { lifeOf, type LifeEvent } from './lifespan.js';
import { attitudeAt, relationActiveAt, relationEntries, worldPositions } from './relations.js';
import { stateOf, type Since } from './states.js';
import type { PriorEntity, PriorKnown, PriorLife, PriorRelation, PriorSource, SeriesContext, SeriesLink } from './types.js';

/** Kurz für die Spalte der Szene („3.2“): „Ep. 1“ */
export const priorLabel = (episode: number): string => `Ep. ${episode}`;

/** „Episode 1 („Der Getreidedampfer“)“ – woher ein Stand der Vorgeschichte kommt */
export const priorText = (p: PriorSource): string => (p.scene ? `Episode ${p.episode} („${p.scene}“)` : `Episode ${p.episode}`);

const priorCache = new WeakMap<StoryCtx, Map<string, PriorEntity>>();

/** Stand eines Elements aus früheren Episoden; fehlt er, steht es in keiner früheren (bzw. die Geschichte in keiner Serie) */
export function priorOf(ctx: StoryCtx, entityId: string): PriorEntity | undefined {
  let m = priorCache.get(ctx);
  if (!m) priorCache.set(ctx, (m = new Map((ctx.story.series?.entities ?? []).map((p) => [p.entityId, p]))));
  return m.get(entityId);
}

const linkCache = new WeakMap<StoryCtx, Map<string, SeriesLink>>();

/** In welchen Episoden ein Element steht und wo sein Original; fehlt es, steht es nur hier */
export function linkOf(ctx: StoryCtx, entityId: string): SeriesLink | undefined {
  let m = linkCache.get(ctx);
  if (!m) linkCache.set(ctx, (m = new Map((ctx.story.series?.links ?? []).map((l) => [l.entityId, l]))));
  return m.get(entityId);
}

/** Ist das Element hier eine Referenz (sein Original steht in einer anderen Episode)? Dann die Nummer der Episode des Originals */
export function referenceOf(ctx: StoryCtx, entityId: string): number | null {
  const l = linkOf(ctx, entityId);
  return l && ctx.story.series && l.origin !== ctx.story.series.number ? l.origin : null;
}

/** Die Vorgeschichte der Geschichte (null: keine) */
export const seriesOf = (ctx: StoryCtx): SeriesContext | null => ctx.story.series ?? null;

/** Titel einer Szene der Geschichte (leer ohne) */
const sceneTitle = (ctx: StoryCtx, sceneId: string): string => String(ctx.nodes.get(sceneId)?.fields.title ?? '').trim();

/** Herkunft eines Werts: aus der Vorgeschichte übernommen bzw. aus einer Szene dieser Episode */
const sourceOf = (ctx: StoryCtx, episode: number, s: { sceneId: string; prior?: PriorSource }): PriorSource => s.prior ?? { episode, scene: sceneTitle(ctx, s.sceneId) };

const lifeSource = (ctx: StoryCtx, episode: number, l: LifeEvent): PriorLife => ({ ...sourceOf(ctx, episode, l), span: l.span, approx: l.approx });

/** Das, was am Ende einer Episode feststeht (IDs dieser Geschichte): die Vorgeschichte der nächsten */
export interface SeriesEnd {
  entities: PriorEntity[];
  relations: PriorRelation[];
  knowledge: PriorKnown[];
}

const isLibrary = (ctx: StoryCtx, typeId: string): boolean => {
  const c = ctx.types.get(typeId)?.category;
  return c === 'object' || c === 'place';
};

/**
 * Stand am Ende dieser Geschichte als Episode `episode` – samt allem, was sie selbst aus früheren Episoden übernommen hat: je Element der
 * Bibliothek Verfassung, Ort, Besitz, Merkmale und Lebensdaten; die Beziehungen, die am Ende gelten (mit der Haltung am Ende); das Wissen
 * der Figuren und des Lesers.
 */
export function seriesEnd(ctx: StoryCtx, episode: number): SeriesEnd {
  const entities: PriorEntity[] = [];
  for (const e of ctx.story.entities) {
    if (!isLibrary(ctx, e.typeId)) continue;
    const s = stateOf(ctx, e.id);
    const life = lifeOf(ctx, e.id);
    const at = <T>(x: Since<T>): PriorSource & { value: T } => ({ ...sourceOf(ctx, episode, x), value: x.value });
    const p: PriorEntity = { entityId: e.id };
    if (s.condition) p.condition = at(s.condition);
    if (s.place) p.place = at(s.place);
    if (s.holder) p.holder = at(s.holder);
    if (s.traits.length > 0) p.traits = s.traits.map((t) => ({ ...sourceOf(ctx, episode, t), trait: t.trait, value: t.value }));
    if (life.born) p.born = lifeSource(ctx, episode, life.born);
    if (life.died) p.died = lifeSource(ctx, episode, life.died);
    entities.push(p);
  }

  const world = worldPositions(ctx);
  const end = Number.MAX_SAFE_INTEGER;
  const relations: PriorRelation[] = [];
  for (const { sourceId, relation: r } of relationEntries(ctx)) {
    // endet die Beziehung in dieser Episode, gilt sie in der nächsten nicht mehr
    if (!r.inherited && !relationActiveAt(r, end, world)) continue;
    const attitude = r.inherited ? r.attitude : attitudeAt(r, end, world);
    relations.push({
      sourceId,
      relation: {
        id: r.id,
        kind: r.kind,
        targetId: r.targetId,
        ...(r.forms ? { forms: r.forms } : {}),
        ...(attitude && Object.keys(attitude).length > 0 ? { attitude } : {}),
        ...(r.minutes ? { minutes: r.minutes } : {}),
        inherited: r.inherited ?? episode,
      },
    });
  }

  const knowledge: PriorKnown[] = knowledgeOf(ctx).flatMap((k) =>
    k.about === null ? [] : [{ ...(k.prior ?? { episode, scene: sceneTitle(ctx, k.sceneId) }), knower: k.knower, about: k.about, text: k.text }],
  );
  return { entities, relations, knowledge };
}

/**
 * Überträgt einen Stand in die IDs einer anderen Episode (`to`: ID dort bzw. null, wenn es das Element dort nicht gibt). Was ein Element
 * betrifft, das es dort nicht gibt, fällt weg; ein Gegenstand bei jemandem, den es dort nicht gibt, ist bei niemandem bekannt.
 */
export function remapSeriesEnd(end: SeriesEnd, to: (id: string) => string | null): SeriesEnd {
  const entities = end.entities.flatMap((p): PriorEntity[] => {
    const entityId = to(p.entityId);
    if (!entityId) return [];
    const out: PriorEntity = { ...p, entityId };
    const place = p.place ? to(p.place.value) : null;
    if (p.place) {
      if (place) out.place = { ...p.place, value: place };
      else delete out.place;
    }
    if (p.holder) {
      const holder = p.holder.value === null ? null : to(p.holder.value);
      if (p.holder.value === null || holder) out.holder = { ...p.holder, value: holder };
      else delete out.holder;
    }
    return [out];
  });
  const relations = end.relations.flatMap((r): PriorRelation[] => {
    const sourceId = to(r.sourceId);
    const targetId = to(r.relation.targetId);
    return sourceId && targetId ? [{ sourceId, relation: { ...r.relation, targetId } }] : [];
  });
  const knowledge = end.knowledge.flatMap((k): PriorKnown[] => {
    const about = to(k.about);
    const knower = k.knower === null ? null : to(k.knower);
    return about && (k.knower === null || knower) ? [{ ...k, about, knower }] : [];
  });
  return { entities, relations, knowledge };
}
